import { v4 as uuidv4 } from 'uuid';
import { getDB, SyncMutation, isCacheUnavailableError } from './db';
import { apiClient } from '../../api/client';
import {
  classifyFailure,
  describeFailure,
  isExhausted,
  nextFailureState,
} from './retry-policy';

export async function enqueueMutation(
  entity: SyncMutation['entity'],
  action: SyncMutation['action'],
  id: string,
  data: Record<string, any>,
  version?: number
): Promise<SyncMutation> {
  // Write path — intentionally NOT swallowed. Enqueuing is the save when there
  // is no connection, so a caller that treats failure as success has silently
  // dropped the user's work. `useOfflineMutation` translates this rejection
  // into a message the field screens show.
  const db = await getDB();
  const mutation: SyncMutation = {
    clientMutationId: uuidv4(),
    entity,
    action,
    id,
    data,
    version,
    timestamp: new Date().toISOString(),
    status: 'pending',
    retryCount: 0,
  };
  await db.put('syncQueue', mutation);
  return mutation;
}

/** Open the queue, or report it as empty when the cache is sealed. */
async function openQueue() {
  // Read path. An unreachable queue holds no mutations we can act on, and the
  // callers — the status bar's counter and `flushQueue` — both do the right
  // thing with an empty list: show nothing pending, push nothing. Rejecting
  // would instead break the sync cycle for a condition the amber banner is
  // already telling the user about.
  try {
    return await getDB();
  } catch (err) {
    if (isCacheUnavailableError(err)) {
      console.warn('[sync-queue] Cache unavailable; treating the queue as empty.');
      return null;
    }
    throw err;
  }
}

export async function getPendingMutations(): Promise<SyncMutation[]> {
  const db = await openQueue();
  if (!db) return [];

  const all = await db.getAllFromIndex('syncQueue', 'by-status', 'pending');
  // Error rows that still have attempts left, per the failure kind that put
  // them there. This used to be a flat `retryCount < 3`, which is where the
  // dropped work went — see `retry-policy` for why the kind decides.
  const errors = await db.getAllFromIndex('syncQueue', 'by-status', 'error');
  const retryable = errors.filter((m) => !isExhausted(m.failureKind, m.retryCount));
  return [...all, ...retryable].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
}

export async function getPendingCount(): Promise<number> {
  const mutations = await getPendingMutations();
  return mutations.length;
}

/**
 * Work that has run out of retries and is waiting on a human.
 *
 * These are deliberately NOT in `getPendingMutations` — they must not be
 * re-POSTed on every cycle — but they are equally deliberately not gone. They
 * are the red bar.
 *
 * Also sweeps up `error` rows that are exhausted under the current policy but
 * were written before it existed (no `failureKind`). Those are exactly the
 * mutations the old `retryCount < 3` filter abandoned in place, and this is
 * what surfaces them on the first run after upgrading.
 */
export async function getFailedMutations(): Promise<SyncMutation[]> {
  const db = await openQueue();
  if (!db) return [];

  const failed = await db.getAllFromIndex('syncQueue', 'by-status', 'failed');
  const errors = await db.getAllFromIndex('syncQueue', 'by-status', 'error');
  const stranded = errors.filter((m) => isExhausted(m.failureKind, m.retryCount));
  return [...failed, ...stranded].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
}

/**
 * The newest REFUSED save of one ticket still in the queue, with the lines it
 * sent and the server's per-row reasons — or null. "Refused" is the server
 * having read it and said no (`failureKind: 'rejected'`), retried or not; a
 * network failure is not a refusal and carries no reasons.
 */
export async function getRejectedTicketSave(
  ticketId: string
): Promise<{ lineItems: any[]; details: NonNullable<SyncMutation['lastErrorDetails']> } | null> {
  const db = await openQueue();
  if (!db) return null;
  const failed = await db.getAllFromIndex('syncQueue', 'by-status', 'failed');
  const errors = await db.getAllFromIndex('syncQueue', 'by-status', 'error');
  const mine = [...failed, ...errors]
    .filter((m) => m.entity === 'tickets' && m.id === ticketId && m.failureKind === 'rejected' && m.lastErrorDetails?.length)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  const latest = mine[0];
  if (!latest) return null;
  const lineItems = latest.data?.lineItems ?? latest.data?.line_items;
  return { lineItems: Array.isArray(lineItems) ? lineItems : [], details: latest.lastErrorDetails! };
}

export async function getFailedCount(): Promise<number> {
  return (await getFailedMutations()).length;
}

/**
 * Put exhausted mutations back in the queue at the user's request.
 *
 * The retry counter resets: a person tapping "Try again" is new information
 * (they may have fixed the underlying problem, or simply moved somewhere with
 * signal), and continuing to count against the old budget would make the
 * button a no-op.
 */
export async function retryFailedMutations(): Promise<number> {
  const db = await openQueue();
  if (!db) return 0;

  const failed = await getFailedMutations();
  for (const mut of failed) {
    mut.status = 'pending';
    mut.retryCount = 0;
    mut.failureKind = undefined;
    await db.put('syncQueue', mut);
  }
  return failed.length;
}

/** Discard an exhausted mutation for good — only ever from an explicit tap. */
export async function discardFailedMutation(clientMutationId: string): Promise<void> {
  const db = await openQueue();
  if (!db) return;
  await db.delete('syncQueue', clientMutationId);
}

export async function flushQueue(): Promise<{
  applied: any[];
  conflicts: any[];
  errors: any[];
}> {
  const mutations = await getPendingMutations();
  if (!mutations.length) return { applied: [], conflicts: [], errors: [] };

  // Unreachable while the cache is sealed — `getPendingMutations` returns []
  // in that state and we have already returned above. Left unguarded on
  // purpose: if we somehow got here with mutations in hand, we are about to
  // POST them to /sync/push and then be unable to record the outcome, and
  // failing before the request is the safe order.
  const db = await getDB();

  try {
    const response = await apiClient.post('/sync/push', {
      mutations: mutations.map((m) => ({
        clientMutationId: m.clientMutationId,
        entity: m.entity,
        action: m.action,
        id: m.id,
        data: m.data,
        version: m.version,
        timestamp: m.timestamp,
      })),
    });

    const result = response.data;

    // Process applied mutations
    for (const a of result.applied || []) {
      const mut = mutations.find((m) => m.clientMutationId === a.clientMutationId);
      if (mut) {
        mut.status = 'applied';
        mut.failureKind = undefined;
        mut.lastError = undefined;
        mut.lastErrorDetails = undefined;
        await db.put('syncQueue', mut);
        // Update local IndexedDB with server record
        if (a.record && mut.entity) {
          const store = mut.entity === 'tickets' ? 'tickets'
            : mut.entity === 'jobs' ? 'jobs'
            : mut.entity === 'equipment' ? 'equipment'
            : 'customers';
          await db.put(store, a.record);
        }
      }
    }

    // Process conflicts — replace local with server truth and log details
    for (const c of result.conflicts || []) {
      const mut = mutations.find((m) => m.clientMutationId === c.clientMutationId);
      if (mut) {
        mut.status = 'conflict';
        await db.put('syncQueue', mut);
        if (c.serverRecord && mut.entity) {
          const store = mut.entity === 'tickets' ? 'tickets'
            : mut.entity === 'jobs' ? 'jobs'
            : mut.entity === 'equipment' ? 'equipment'
            : 'customers';
          await db.put(store, c.serverRecord);
        }
        // Write conflict details to conflictLog for review
        await db.put('conflictLog', {
          clientMutationId: mut.clientMutationId,
          entity: mut.entity,
          id: mut.id,
          localData: mut.data,
          serverRecord: c.serverRecord || {},
          reason: c.reason || 'Version conflict — server data was newer',
          timestamp: new Date().toISOString(),
          dismissed: false,
        });
      }
    }

    // Process per-mutation errors. A 200 body listing a mutation here means the
    // server read it and refused it — `rejected`, not something to try until
    // the heat death of the universe.
    for (const e of result.errors || []) {
      const mut = mutations.find((m) => m.clientMutationId === e.clientMutationId);
      if (mut) {
        const next = nextFailureState('rejected', mut.retryCount);
        mut.status = next.status;
        mut.retryCount = next.retryCount;
        mut.failureKind = 'rejected';
        mut.lastError = e.error || e.message || 'Rejected by the server';
        mut.lastErrorDetails = Array.isArray(e.details) ? e.details : undefined;
        await db.put('syncQueue', mut);
      }
    }

    return result;
  } catch (err: any) {
    // The whole push failed. Previously this only reverted to `pending` when
    // `!err.response` and did nothing whatsoever otherwise — which is how a
    // 400 came to be re-POSTed on every sync cycle forever, its `retryCount`
    // pinned at 0 because only the 200-body path ever incremented it.
    //
    // Now every failure records why, and the kind decides whether there is a
    // next attempt: a network failure costs nothing and stays pending, a 4xx
    // burns an attempt and eventually lands in `failed` where the user sees it.
    const kind = classifyFailure(err);
    const message = describeFailure(err);
    for (const mut of mutations) {
      const next = nextFailureState(kind, mut.retryCount);
      mut.status = next.status;
      mut.retryCount = next.retryCount;
      mut.failureKind = kind;
      mut.lastError = message;
      await db.put('syncQueue', mut);
    }
    throw err;
  }
}

/** Clean up applied mutations after 24h, conflicts after 7 days */
export async function cleanupQueue(): Promise<void> {
  // Housekeeping, and the last step of `performSync`. There is nothing to tidy
  // in a cache we cannot open, and rejecting here would fail a sync whose push
  // and pull had both already succeeded.
  const db = await openQueue();
  if (!db) return;

  const all = await db.getAll('syncQueue');
  const appliedCutoff = Date.now() - 24 * 60 * 60 * 1000;
  const conflictCutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
  for (const m of all) {
    const ts = new Date(m.timestamp).getTime();
    if (m.status === 'applied' && ts < appliedCutoff) {
      await db.delete('syncQueue', m.clientMutationId);
    }
    if (m.status === 'conflict' && ts < conflictCutoff) {
      await db.delete('syncQueue', m.clientMutationId);
      // Also clean up the conflict log entry
      try { await db.delete('conflictLog', m.clientMutationId); } catch {}
    }
  }
  // `failed` is never swept. It is unsynced work the user has been told about
  // and has not dealt with; ageing it out would be the silent drop this whole
  // change exists to remove. It leaves only by the user's tap — retried, or
  // discarded deliberately.
}
